import { LeftPanelType, RightPanelType } from "@kuika/kuika-cl-model";
import type { IWorkFlowDto } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { WorkFlowService } from "../../../../services/workflow-service";
import { navigateToWorkflowView, openWorkflowForEditing } from "../../designer-view-actions";
import { listDesignerWorkflowSteps, selectDesignerWorkflowStep } from "../../designer-workflow-step-actions";
import {
  workflowCreateUserTaskAction,
  workflowCreateSystemStepAction,
  workflowUpdateUserTaskNameAction,
  workflowUpdateSystemStepNameAction,
  workflowUpdateStepPropertiesAction,
  workflowDeleteElementAction,
  workflowAddGatewayAction,
  workflowCreateGatewayPathAction,
  workflowSetDefaultPathAction,
  workflowInitializePathConditionAction,
  workflowConfigureMultiInstanceAction,
  workflowInitializeMiCancelConditionAction,
  workflowConfigureMiAggregationAction,
  workflowAddLaneAction,
  workflowDeleteLaneAction,
  workflowUpdateLaneNameAction,
  workflowSetLaneAssignmentAction,
  workflowAddTimerIntermediateEventAction,
  workflowDeleteTimerIntermediateEventAction,
  workflowSetTimerEventDueDateAction,
  workflowAttachBoundaryEventAction,
  workflowDeleteBoundaryEventAction,
  workflowConfigureBoundaryEventAction,
  workflowAddSubprocessAction,
  workflowDeleteSubprocessAction,
  workflowUpdateSubprocessNameAction,
  workflowAddLinkedProcessAction,
  workflowDeleteLinkedProcessAction,
  workflowUpdateLinkedProcessNameAction,
  workflowConfigureLinkedProcessParametersAction,
  workflowCreateBusinessObjectParameterAction,
  workflowCreateInternalParameterAction,
  workflowUpdateProcessParameterAction,
  workflowDeleteProcessParameterAction,
  workflowAddUserInteractionAction,
  workflowSetUserTaskDueDateAction,
  workflowAddPreActionToUserTaskAction,
  workflowSetUserTaskNotificationPreferenceAction,
  workflowAddActionToSystemStepAction,
  workflowMapStepOutputParameterAction,
  workflowUpdatePathNameAction,
  workflowAssignScreenToUserTaskAction,
  workflowAutoLayoutAction,
  workflowGetLintIssuesAction
} from "../../designer-workflow-step-actions";
import { listWorkflowVisibleActions } from "../../workflow-action-resolver";
import { isBpmnModelerReady, waitForBpmnModelerReady } from "../../workflow-modeler-diagnostics";
import {
  buildDefaultWorkflowJson,
  buildDefaultWorkflowXml,
  DEFAULT_WORKFLOW_SKELETON_IDS,
  findWorkflowNameConflict,
  validateWorkflowName
} from "../../workflow-templates";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

/**
 * Shape used by workflow handlers. Matches `ScreenDesignContextProvider` at
 * runtime:
 *
 *   - **State data** lives under `partition.state.*` (workFlowList, selectedWf,
 *     project, screenList, datasources, ...).
 *   - **Methods** live as instance properties on the partition itself:
 *     `sd.workflowProvider.loadWorkFlowList(...)` — NOT
 *     `sd.workflowProvider.state.loadWorkFlowList(...)` (that's the
 *     default-state stub, a Promise that never resolves).
 *   - The `designSituationProvider` owns UI panel setters.
 *
 * Optional top-level fields (`project`, `datasources`, etc.) are retained as
 * fallbacks so test mocks built around a flat aggregated shape still work.
 */
interface SdLike {
  projectProvider?: { state?: { project?: { id?: string } } };
  workflowProvider?: {
    state?: {
      workFlowList?: { id: string; name?: string; workFlowName?: string }[];
      selectedWf?: { id?: string };
    };
    loadWorkFlowList?: (projectId: string) => Promise<void>;
    setSelectedWf?: (workflowId: string) => void | Promise<void>;
    clearSelectedWf?: () => void | Promise<void>;
    setCreatingWfActive?: (active: boolean) => void;
  };
  designSituationProvider?: {
    setActiveWorkFlowLeftPanel?: (val: unknown) => void;
    setActiveWorkFlowRightPanel?: (val: unknown) => void;
  };
  screenProvider?: { state?: { screenList?: { id?: string; name: string }[] } };
  datasourceProvider?: { state?: { datasources?: { id?: string; name: string }[] } };
  state?: { project?: { id?: string }; workFlowList?: { id: string; name?: string; workFlowName?: string }[] };
  // Legacy/flat fallbacks for older integrations and tests
  project?: { id?: string };
  workFlowList?: { id: string; name?: string; workFlowName?: string }[];
  screenList?: { id?: string; name: string }[];
  datasources?: { id?: string; name: string }[];
}

function getProjectId(sd: SdLike): string | undefined {
  return sd.projectProvider?.state?.project?.id || sd.state?.project?.id || sd.project?.id;
}

function getWorkflowList(sd: SdLike): { id: string; name?: string; workFlowName?: string }[] {
  return sd.workflowProvider?.state?.workFlowList || sd.workFlowList || sd.state?.workFlowList || [];
}

function getScreenList(sd: SdLike): { id?: string; name: string }[] | undefined {
  return sd.screenProvider?.state?.screenList || sd.screenList;
}

function getDatasources(sd: SdLike): { id?: string; name: string }[] | undefined {
  return sd.datasourceProvider?.state?.datasources || sd.datasources;
}

// Instance methods on workflowProvider (NOT state.* — those are stubs).
function getLoadWorkFlowList(sd: SdLike): ((projectId: string) => Promise<void>) | undefined {
  return sd.workflowProvider?.loadWorkFlowList;
}

// `setSelectedWf` takes an ID STRING — never a DTO. The old `string | IWorkFlowDto`
// widening here was fabricated (the provider never accepted a DTO) and is what let
// `setSelectedWf(res.data)` compile: the object stringified into the request URL,
// the fetch 404'd, and the selection silently never committed. (KOA-17362)
function getSetSelectedWf(sd: SdLike): ((workflowId: string) => void | Promise<void>) | undefined {
  return sd.workflowProvider?.setSelectedWf;
}

function getClearSelectedWf(sd: SdLike): (() => void | Promise<void>) | undefined {
  return sd.workflowProvider?.clearSelectedWf;
}

function getSetCreatingWfActive(sd: SdLike): ((active: boolean) => void) | undefined {
  return sd.workflowProvider?.setCreatingWfActive;
}

function getSetActiveLeftPanel(sd: SdLike): ((val: unknown) => void) | undefined {
  return sd.designSituationProvider?.setActiveWorkFlowLeftPanel;
}

function getSetActiveRightPanel(sd: SdLike): ((val: unknown) => void) | undefined {
  return sd.designSituationProvider?.setActiveWorkFlowRightPanel;
}

export const workflowTools: ToolHandlerGroup = {
  sync: {
    ui_project_list_workflows: () => {
      const sd = getSdInstance();
      const workFlowList: any[] = sd.workflowProvider?.state?.workFlowList || sd.state?.workFlowList || [];

      if (workFlowList.length === 0) {
        return { workflows: [], message: "No workflows found in this project." };
      }

      // Mark which workflow is currently OPEN in the BPMN editor so the
      // workflow-builder agent edits it for an "add to the flow" request
      // instead of spawning a brand-new workflow (KOA-17362 follow-up).
      const openWorkflowId = sd.workflowProvider?.state?.selectedWf?.id;

      const workflows = workFlowList.map((wf: any) => ({
        id: wf.id,
        name: wf.name || wf.workFlowName,
        isActive: wf.isActive ?? null,
        isOpen: openWorkflowId != null && String(wf.id) === String(openWorkflowId)
      }));

      const openWorkflow = workflows.find((wf) => wf.isOpen);
      return {
        workflows,
        count: workflows.length,
        ...(openWorkflow ? { openWorkflowId: openWorkflow.id, openWorkflowName: openWorkflow.name } : {})
      };
    },

    ui_project_list_workflow_steps: (input) => {
      return listDesignerWorkflowSteps(getSdInstance(), input);
    },

    // Deterministic, workflow-valid action catalog for STEP_ACTION discovery
    // (KOA-17362). Replaces the fuzzy `search_component_metadata` vector path
    // for the WorkflowBuilder — returns only actions actually attachable to a
    // workflow step (UI-only actions filtered out by `isActionDefinitionVisible`).
    ui_project_list_actions: (input) => {
      const query: string | undefined =
        typeof input.query === "string" ? input.query : typeof input.text === "string" ? input.text : undefined;
      const { actions, total, truncated } = listWorkflowVisibleActions(getSdInstance(), query);
      const note: string = truncated
        ? `${total} workflow-valid actions exist; showing the first ${actions.length}${query ? ` (most relevant to "${query}" first)` : ""}. Narrow with a more specific 'query'.`
        : `Full workflow-valid action catalog (${actions.length})${query ? `, most relevant to "${query}" first` : ""}. Pick the best-matching name yourself — the list is NOT filtered by your query, it is only re-ordered, so scan all of it.`;
      return { actions, count: actions.length, total, note };
    },

    ui_workflow_create_user_task: (input) => workflowCreateUserTaskAction(getSdInstance(), input),
    ui_workflow_create_system_step: (input) => workflowCreateSystemStepAction(getSdInstance(), input),
    ui_workflow_update_user_task_name: (input) => workflowUpdateUserTaskNameAction(getSdInstance(), input),
    ui_workflow_update_system_step_name: (input) => workflowUpdateSystemStepNameAction(getSdInstance(), input),
    ui_workflow_update_step_properties: (input) => workflowUpdateStepPropertiesAction(getSdInstance(), input),
    ui_workflow_delete_element: (input) => workflowDeleteElementAction(getSdInstance(), input),
    ui_workflow_add_gateway: (input) => workflowAddGatewayAction(getSdInstance(), input),
    ui_workflow_create_gateway_path: (input) => workflowCreateGatewayPathAction(getSdInstance(), input),
    ui_workflow_set_default_path: (input) => workflowSetDefaultPathAction(getSdInstance(), input),
    ui_workflow_initialize_path_condition: (input) => workflowInitializePathConditionAction(getSdInstance(), input),
    ui_workflow_configure_multi_instance: (input) => workflowConfigureMultiInstanceAction(getSdInstance(), input),
    ui_workflow_initialize_mi_cancel_condition: (input) =>
      workflowInitializeMiCancelConditionAction(getSdInstance(), input),
    ui_workflow_configure_mi_aggregation: (input) => workflowConfigureMiAggregationAction(getSdInstance(), input),
    ui_workflow_add_lane: (input) => workflowAddLaneAction(getSdInstance(), input),
    ui_workflow_delete_lane: (input) => workflowDeleteLaneAction(getSdInstance(), input),
    ui_workflow_update_lane_name: (input) => workflowUpdateLaneNameAction(getSdInstance(), input),
    ui_workflow_set_lane_assignment: (input) => workflowSetLaneAssignmentAction(getSdInstance(), input),
    ui_workflow_add_timer_intermediate_event: (input) =>
      workflowAddTimerIntermediateEventAction(getSdInstance(), input),
    ui_workflow_delete_timer_intermediate_event: (input) =>
      workflowDeleteTimerIntermediateEventAction(getSdInstance(), input),
    ui_workflow_set_timer_event_due_date: (input) => workflowSetTimerEventDueDateAction(getSdInstance(), input),
    ui_workflow_attach_boundary_event: (input) => workflowAttachBoundaryEventAction(getSdInstance(), input),
    ui_workflow_delete_boundary_event: (input) => workflowDeleteBoundaryEventAction(getSdInstance(), input),
    ui_workflow_configure_boundary_event: (input) => workflowConfigureBoundaryEventAction(getSdInstance(), input),
    ui_workflow_add_subprocess: (input) => workflowAddSubprocessAction(getSdInstance(), input),
    ui_workflow_delete_subprocess: (input) => workflowDeleteSubprocessAction(getSdInstance(), input),
    ui_workflow_update_subprocess_name: (input) => workflowUpdateSubprocessNameAction(getSdInstance(), input),
    ui_workflow_add_linked_process: (input) => workflowAddLinkedProcessAction(getSdInstance(), input),
    ui_workflow_delete_linked_process: (input) => workflowDeleteLinkedProcessAction(getSdInstance(), input),
    ui_workflow_update_linked_process_name: (input) => workflowUpdateLinkedProcessNameAction(getSdInstance(), input),
    ui_workflow_configure_linked_process_parameters: (input) =>
      workflowConfigureLinkedProcessParametersAction(getSdInstance(), input),
    ui_workflow_create_business_object_parameter: (input) =>
      workflowCreateBusinessObjectParameterAction(getSdInstance(), input),
    ui_workflow_create_internal_parameter: (input) => workflowCreateInternalParameterAction(getSdInstance(), input),
    ui_workflow_update_process_parameter: (input) => workflowUpdateProcessParameterAction(getSdInstance(), input),
    ui_workflow_delete_process_parameter: (input) => workflowDeleteProcessParameterAction(getSdInstance(), input),
    ui_workflow_add_user_interaction: (input) => workflowAddUserInteractionAction(getSdInstance(), input),
    ui_workflow_set_user_task_due_date: (input) => workflowSetUserTaskDueDateAction(getSdInstance(), input),
    ui_workflow_add_pre_action_to_user_task: (input) => workflowAddPreActionToUserTaskAction(getSdInstance(), input),
    ui_workflow_set_user_task_notification_preference: (input) =>
      workflowSetUserTaskNotificationPreferenceAction(getSdInstance(), input),
    ui_workflow_add_action_to_system_step: (input) => workflowAddActionToSystemStepAction(getSdInstance(), input),
    ui_workflow_map_step_output_parameter: (input) => workflowMapStepOutputParameterAction(getSdInstance(), input),
    ui_workflow_update_path_name: (input) => workflowUpdatePathNameAction(getSdInstance(), input),
    ui_workflow_assign_screen_to_user_task: (input) => workflowAssignScreenToUserTaskAction(getSdInstance(), input)
  },

  async: {
    ui_workflow_auto_layout: async (input) => workflowAutoLayoutAction(getSdInstance(), input),

    ui_workflow_get_lint_issues: async (input) => workflowGetLintIssuesAction(getSdInstance(), input),

    ui_project_navigate_to_workflow: async (input) => {
      const { workflowId } = input;
      if (!workflowId) return { error: "workflowId is required" };

      const sd = getSdInstance();
      const currentSelectedWf = sd.workflowProvider?.state?.selectedWf;

      // Skip the re-open ONLY when the workflow is selected AND its modeler is
      // genuinely mounted. Skipping on selectedWf-match alone (the old guard)
      // locked out recovery: create_workflow sets selectedWf but its mount-time
      // modeler build can race/miss on a cold first-run, so the modeler is never
      // built and every later navigate no-op'd → the whole turn saw "BPMN Modeler
      // is not ready". When selectedWf matches but the modeler is absent, fall
      // through to openWorkflowForEditing below to force the (re)build. (KOA-17362)
      if (currentSelectedWf && String(currentSelectedWf.id) === String(workflowId) && isBpmnModelerReady()) {
        return {
          success: true,
          message: "Workflow is already open. Skipped navigation to prevent React unmount loop.",
          workflowId: String(workflowId),
          workflowName: currentSelectedWf.name
        };
      }

      const workFlowList: any[] = sd.workflowProvider?.state?.workFlowList || sd.state?.workFlowList || [];
      const workflow = workFlowList.find((wf: any) => String(wf.id) === String(workflowId));

      if (!workflow) {
        const available = workFlowList.map((wf: any) => `${wf.id} (${wf.name || wf.workFlowName})`);
        return {
          error: `Workflow "${workflowId}" not found.`,
          availableWorkflows: available
        };
      }

      await openWorkflowForEditing(sd, workflowId, {
        ensureView: true,
        setRootSelection: true
      });

      return {
        success: true,
        message: `Opened workflow "${workflow.name || workflow.workFlowName}" in the BPMN editor.`,
        workflowId: String(workflow.id),
        workflowName: workflow.name || workflow.workFlowName
      };
    },

    ui_project_select_workflow_step: async (input) => {
      return selectDesignerWorkflowStep(getSdInstance(), input);
    },

    ui_project_create_workflow: async (input) => {
      const name = typeof input?.name === "string" ? input.name.trim() : "";
      const invalid = validateWorkflowName(name);
      if (invalid) return { error: invalid };

      const sd = getSdInstance() as SdLike;
      const projectId = getProjectId(sd);
      if (!projectId) return { error: "Could not resolve current project id." };

      const conflict = findWorkflowNameConflict({
        name,
        workflows: getWorkflowList(sd),
        screens: getScreenList(sd),
        datasources: getDatasources(sd)
      });
      if (conflict) {
        // A same-named WORKFLOW is NOT a hard error — REUSE it (KOA-17362). The
        // WorkflowBuilder retried create with suffixed names on a collision,
        // spawning duplicate workflows ("…2"/"…3"). Open the existing one and
        // return a reuse-success so the caller edits it instead (the orchestrator
        // singleton guard blocks further creates this turn). A screen/datasource
        // name collision stays a genuine error — those can't be reused as a workflow.
        if (conflict.kind === "workflow") {
          const existing = getWorkflowList(sd).find(
            (wf: { id: string; name?: string; workFlowName?: string }): boolean =>
              (wf.name ?? wf.workFlowName ?? "").toLowerCase() === name.toLowerCase()
          );
          if (existing?.id) {
            const openExisting = input?.openAfterCreate !== false;
            // Same contract as the create path below: `opened` reports the OBSERVED
            // canvas, never the requested one.
            let reusedCanvasReady: boolean = false;
            if (openExisting) {
              // openWorkflowForEditing needs the real instance type (mirrors the
              // navigate_to_workflow handler) — `sd` above is cast to SdLike.
              await openWorkflowForEditing(getSdInstance(), String(existing.id), {
                ensureView: true,
                setRootSelection: true
              });
              reusedCanvasReady = await waitForBpmnModelerReady();
            }
            return {
              success: true,
              reused: true,
              message: `Workflow "${name}" already exists — reused it (no duplicate created). Run ui_workflow_dsl LIST to see its current nodes before editing.`,
              workflowId: String(existing.id),
              workflowName: existing.name ?? existing.workFlowName ?? name,
              opened: openExisting && reusedCanvasReady
            };
          }
        }
        return { error: `Cannot use "${name}" — a ${conflict.kind} with that name already exists.` };
      }

      const dto: IWorkFlowDto = {
        id: Guid.create().toString(),
        name,
        displayName: typeof input?.displayName === "string" ? input.displayName : name,
        description: typeof input?.description === "string" ? input.description : "",
        kProject_Id: projectId,
        json: buildDefaultWorkflowJson(name),
        xml: buildDefaultWorkflowXml(name)
      };

      const res = await WorkFlowService.Create(dto);
      if (!res || res.status !== 200) {
        return { error: `Backend rejected create (status: ${res?.status ?? "unknown"}).` };
      }

      const loadList = getLoadWorkFlowList(sd);
      if (loadList) await loadList(projectId);

      const openAfter = input?.openAfterCreate !== false;
      // `opened` is a CLAIM the caller ACTS ON: the tool docs tell the model not to
      // call navigate_to_workflow when the workflow is already open. Echoing the
      // input flag back therefore does not merely misreport — it actively steers the
      // model away from the one call that would have fixed a view that never landed,
      // which is how KOA-18136's build died with an empty workflow. Verify instead of
      // asserting, and verify the thing that actually matters: not "did we call the
      // navigation helper" but "is the canvas the next ui_workflow_dsl needs alive".
      // The wait is not new latency — the DSL pays exactly this mount on its first
      // graph command; doing it here just moves the cost to where the truth is told.
      let canvasReady: boolean = false;
      if (openAfter) {
        // SELECT the new workflow BEFORE switching to the BPMN editor view.
        // Ordering is load-bearing (KOA-17362): navigateToWorkflowView mounts
        // WorkFlowWindow, whose componentDidMount runs ensureModelerBuilt — which
        // builds the BPMN modeler ONLY when a workflow is ALREADY selected at
        // mount. The old order (navigate FIRST, then setSelectedWf) mounted the
        // canvas with NO selection, so componentDidMount could not build and the
        // modeler build was left to the fragile update-path re-render race
        // (checkNeedRender + creatingWfActive flush timing). On the compound
        // (screens-first → workflow-last) build that race lost every time: the
        // modeler never mounted (window.bpmnModeler undefined for 45s → DSL
        // atomic-aborted → whole workflow build failed). Selecting first makes
        // the deterministic mount-time build fire, so the modeler is ready when
        // the DSL runs.
        // Await the lock release too: un-awaited, the "release all locks" call can
        // land AFTER the fresh lock below and leave the canvas read-only.
        await getClearSelectedWf(sd)?.();
        // Selection must be COMMITTED before the view switch so WorkFlowWindow
        // mounts with selectedWf set — setSelectedWf is awaitable and resolves
        // only after the state commit. Pass the ID: it is an id-keyed setter, and
        // handing it the DTO (the old bug) stringified to "[object Object]", 404'd
        // the load, and left selectedWf undefined — so the BPMN modeler was never
        // built and every DSL graph command aborted "not ready". (KOA-17362)
        await getSetSelectedWf(sd)?.(String(res.data?.id ?? dto.id));
        // navigateToWorkflowView is idempotent (never toggles workFlow→design);
        // it mounts WorkFlowWindow into the already-selected workflow.
        await navigateToWorkflowView(getSdInstance(), { selectLastOpenWorkflow: false, createWhenEmpty: false });
        getSetActiveLeftPanel(sd)?.(LeftPanelType.WorkFlowFieldList);
        getSetActiveRightPanel(sd)?.(RightPanelType.Properties);
        getSetCreatingWfActive(sd)?.(false);
        canvasReady = await waitForBpmnModelerReady();
      }

      return {
        success: true,
        message: `Workflow "${name}" created.`,
        workflowId: dto.id,
        workflowName: name,
        opened: openAfter && canvasReady,
        ...(openAfter && !canvasReady
          ? {
              openWarning:
                `Workflow "${name}" was created, but its BPMN canvas did not finish mounting — it is NOT open yet. ` +
                "Send your ui_workflow_dsl script anyway: the executor re-opens the process-automation view itself " +
                "before running. Do NOT re-create the workflow."
            }
          : {}),
        // Fixed skeleton node ids so the agent reuses the real placeholder id
        // (RENAME the Sample Step) instead of guessing `Activity_1` — the guess
        // that triggered the KOA-17362 delete/recreate spiral.
        skeleton: { ...DEFAULT_WORKFLOW_SKELETON_IDS, sampleStepName: "Sample Step" }
      };
    },

    ui_project_delete_workflow: async (input) => {
      const workflowId = typeof input?.workflowId === "string" ? input.workflowId : "";
      if (!workflowId) return { error: "`workflowId` is required." };

      const sd = getSdInstance() as SdLike;
      const list = getWorkflowList(sd);
      const target = list.find((w) => String(w.id) === workflowId);
      if (!target) {
        return {
          error: `Workflow "${workflowId}" not found.`,
          availableWorkflows: list.map((w) => `${w.id} (${w.name || w.workFlowName})`)
        };
      }

      const ok = await WorkFlowService.Delete(workflowId);
      if (!ok) return { error: `Backend rejected delete of workflow "${workflowId}".` };

      const projectId = getProjectId(sd);
      const loadList = getLoadWorkFlowList(sd);
      if (projectId && loadList) await loadList(projectId);

      // If the deleted workflow was currently open, clear the selection and
      // return the user to the workflow list panel — mirrors what the UI
      // delete dialog does so the designer re-renders correctly.
      const openId = sd.workflowProvider?.state?.selectedWf?.id;
      if (openId && String(openId) === workflowId) {
        await getClearSelectedWf(sd)?.();
        getSetActiveLeftPanel(sd)?.(LeftPanelType.WorkFlowList);
        getSetCreatingWfActive(sd)?.(true);
      }

      return {
        success: true,
        message: `Workflow "${target.name || target.workFlowName || workflowId}" deleted.`,
        workflowId
      };
    },

    ui_project_duplicate_workflow: async (input) => {
      const workflowId = typeof input?.workflowId === "string" ? input.workflowId : "";
      if (!workflowId) return { error: "`workflowId` is required." };

      const sd = getSdInstance() as SdLike;
      const list = getWorkflowList(sd);
      const source = list.find((w) => String(w.id) === workflowId);
      if (!source) {
        return {
          error: `Source workflow "${workflowId}" not found.`,
          availableWorkflows: list.map((w) => `${w.id} (${w.name || w.workFlowName})`)
        };
      }

      // Resolve new name
      let newName = typeof input?.newName === "string" ? input.newName.trim() : "";
      if (newName) {
        const invalid = validateWorkflowName(newName);
        if (invalid) return { error: invalid };
        const conflict = findWorkflowNameConflict({
          name: newName,
          workflows: list,
          screens: getScreenList(sd),
          datasources: getDatasources(sd)
        });
        if (conflict) return { error: `Cannot use "${newName}" — a ${conflict.kind} with that name already exists.` };
      } else {
        const baseName = `${source.name || source.workFlowName || "Workflow"} Copy`;
        let candidate = baseName;
        let suffix = 2;
        while (list.some((w) => (w.name || w.workFlowName || "").toLowerCase() === candidate.toLowerCase())) {
          candidate = `${baseName} ${suffix}`;
          suffix++;
        }
        newName = candidate;
      }

      const res = await WorkFlowService.Duplicate({
        WorkflowIdToDuplicate: workflowId,
        NewWorkflowName: newName
      });
      if (!res || res.status !== 200) {
        return { error: `Backend rejected duplicate (status: ${res?.status ?? "unknown"}).` };
      }

      const projectId = getProjectId(sd);
      const loadList = getLoadWorkFlowList(sd);
      if (projectId && loadList) await loadList(projectId);

      const newId = res.data?.id ?? res.data;
      const openAfter = input?.openAfterDuplicate !== false;
      if (openAfter && newId) {
        getSetSelectedWf(sd)?.(String(newId));
        getSetCreatingWfActive(sd)?.(false);
      }

      return {
        success: true,
        message: `Workflow duplicated as "${newName}".`,
        workflowId: newId,
        workflowName: newName,
        sourceWorkflowId: workflowId,
        opened: openAfter
      };
    }
  }
};
